import {
  AvoidType,
  CO2ClassType,
  EmissionType,
  LoadType,
  TravelMode,
} from "../../api/adHocCommands"
import type { RoutingProvider } from "@/reducers/serviceConfigSlice"
import type { TFunction } from "i18next"

export const availableAvoidOptions: { label: string; value: AvoidType }[] = [
  { label: "Toll Roads", value: "tollRoads" },
  { label: "Motorways", value: "motorways" },
  { label: "Ferries", value: "ferries" },
  { label: "Unpaved Roads", value: "unpavedRoads" },
  { label: "Carpools", value: "carpools" },
  { label: "Border Crossings", value: "borderCrossings" },
  { label: "Tunnels", value: "tunnels" },
  { label: "Car Trains", value: "carTrains" },
  { label: "Low Emission Zones", value: "lowEmissionZones" },
]

export const availableLoadTypes: { label: string; value: LoadType }[] = [
  { label: "Explosive Hazardous Materials", value: "otherHazmatExplosive" },
  { label: "General Hazardous Materials", value: "otherHazmatGeneral" },
  {
    label: "Harmful to Water Hazardous Materials",
    value: "otherHazmatHarmfulToWater",
  },
]

export const travelModes: { label: string; value: TravelMode }[] = [
  { label: "Car", value: "car" },
  { label: "Truck", value: "truck" },
]

export const emissionOptions: { label: string; value: EmissionType }[] = [
  { label: "None", value: "none" },
  { label: "Euro 1", value: "euro1" },
  { label: "Euro 2", value: "euro2" },
  { label: "Euro 3", value: "euro3" },
  { label: "Euro 4", value: "euro4" },
  { label: "Euro 5", value: "euro5" },
  { label: "Euro 6", value: "euro6" },
]

export const co2ClassOptions: { label: string; value: CO2ClassType }[] = [
  { label: "None", value: "none" },
  { label: "Class 1", value: "1" },
  { label: "Class 2", value: "2" },
  { label: "Class 3", value: "3" },
  { label: "Class 4", value: "4" },
  { label: "Class 5", value: "5" },
]

// The `label` fields above stay in English: screens outside the maps area
// show them as they are. Maps screens translate at render with these keys.
const OPTION_LABEL_KEYS = {
  tollRoads: "maps.carRouteOptions.avoid.tollRoads",
  motorways: "maps.carRouteOptions.avoid.motorways",
  ferries: "maps.carRouteOptions.avoid.ferries",
  unpavedRoads: "maps.carRouteOptions.avoid.unpavedRoads",
  carpools: "maps.carRouteOptions.avoid.carpools",
  borderCrossings: "maps.carRouteOptions.avoid.borderCrossings",
  tunnels: "maps.carRouteOptions.avoid.tunnels",
  carTrains: "maps.carRouteOptions.avoid.carTrains",
  lowEmissionZones: "maps.carRouteOptions.avoid.lowEmissionZones",
  otherHazmatExplosive: "maps.carRouteOptions.loadType.otherHazmatExplosive",
  otherHazmatGeneral: "maps.carRouteOptions.loadType.otherHazmatGeneral",
  otherHazmatHarmfulToWater:
    "maps.carRouteOptions.loadType.otherHazmatHarmfulToWater",
  car: "maps.carRouteOptions.travelMode.car",
  truck: "maps.carRouteOptions.travelMode.truck",
  none: "maps.carRouteOptions.none",
} as const

/** Translated label of a route option; unknown values keep their label. */
export const optionLabel = (
  t: TFunction,
  option: { label: string; value: string },
): string => {
  if (!Object.prototype.hasOwnProperty.call(OPTION_LABEL_KEYS, option.value))
    return option.label
  return t(OPTION_LABEL_KEYS[option.value as keyof typeof OPTION_LABEL_KEYS])
}

// "Class 3" and the like: only the word is translated.
export const co2ClassLabel = (
  t: TFunction,
  option: { label: string; value: CO2ClassType },
): string =>
  option.value === "none"
    ? optionLabel(t, option)
    : t("maps.carRouteOptions.co2Class", { number: option.value })

// Options never supported by any routing provider
const ALWAYS_EXCLUDED: ReadonlySet<AvoidType> = new Set([
  "carpools",
  "borderCrossings",
])

// Options only available on PTV (HERE cannot route with these)
const HERE_EXCLUDED: ReadonlySet<AvoidType> = new Set(["motorways"])

// Options only available on HERE (PTV silently ignores these)
const PTV_EXCLUDED: ReadonlySet<AvoidType> = new Set([
  "unpavedRoads",
  "tunnels",
  "lowEmissionZones",
])

export const getAvailableAvoidOptions = <
  T extends { label: string; value: AvoidType }[],
>(
  options: T,
  routingProvider: RoutingProvider = "here",
): T => {
  const providerExcluded =
    routingProvider === "here"
      ? HERE_EXCLUDED
      : routingProvider === "ptv"
        ? PTV_EXCLUDED
        : new Set<AvoidType>()

  return options.filter(
    (option) =>
      !ALWAYS_EXCLUDED.has(option.value) && !providerExcluded.has(option.value),
  ) as T
}
